/**
 * Owner-redirect gate for the setup wizard (consumed by App.tsx). Pure and
 * dependency-free so the offline case is unit-testable — same pattern as
 * `offlineAuthGrace.decideSessionEnd`.
 *
 * The wizard is re-offered to an OWNER whose clinic has no
 * `onboardingCompletedAt`. That answer is only trustworthy when the profile
 * came from the network. Offline, the `['current-user-me']` query is served by
 * `readCurrentUser()` from the replica `users` table — a flat User row from the
 * sync pull (`prisma.user.findMany`, no clinic include) — so `clinic` is ABSENT
 * there, not "unstamped". Reading absent as unstamped bounced every owner into a
 * wizard that cannot submit without a network (Exact Equine, in the field,
 * 2026-09-11). Every unknown fails safe to "no bounce": a missed re-offer costs
 * one survey; a false bounce traps a vet at a farm.
 */

import type { components } from '../types/api.generated';

type ClientClinic = components['schemas']['ClientClinicDto'];

export interface OnboardingGateProfile {
  role?: string | null;
  /**
   * Present (object or null) on the network shape; absent on the replica row. Keyed off the
   * server's response class (fix 36), so the build breaks if /users/me stops sending the stamp.
   */
  clinic?: { onboardingCompletedAt?: ClientClinic['onboardingCompletedAt'] | Date } | null;
  [key: string]: unknown;
}

export interface OnboardingGateInput {
  isAuthenticated: boolean;
  /** Already folds in dev-bypass and A5 grace (App.tsx computes it). */
  emailVerified: boolean;
  /** A5: running on a cached session during the offline grace window. */
  graceMode: boolean;
  /** Effective reachability. Pass `true` when the offline feature is off for this device. */
  online: boolean;
  /** React Query `isError` for the profile fetch (both network and replica failed). */
  checkFailed: boolean;
  profile: OnboardingGateProfile | null | undefined;
}

export function decideNeedsOnboarding({
  isAuthenticated,
  emailVerified,
  graceMode,
  online,
  checkFailed,
  profile,
}: OnboardingGateInput): boolean {
  if (!isAuthenticated || !emailVerified) return false;
  if (graceMode || !online) return false; // an unknown answer must not bounce a working session
  if (checkFailed || !profile) return false;
  if (profile.role !== 'OWNER') return false; // the survey is clinic-level; owners only
  const clinic = profile.clinic;
  if (clinic === undefined || clinic === null) return false; // replica shape, or no clinic: unknown
  return !clinic.onboardingCompletedAt;
}
